// Stock allocations for one event — the Stok tab of the event detail page.
//
// Was the /events/:eventId/stock page; the how-to, the export button and the
// allocation manager moved here unchanged when the detail page arrived.

import { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { EventAllocationsManager } from '@/components/events/EventAllocationsManager';
import { ExportStockButton } from '@/components/events/ExportStockButton';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import type { AppEvent } from '@/types/domain';

const GUIDE_KEY = 'ophth_stock_guide_collapsed';

export function EventStockPanel({ event }: { event: AppEvent }) {
  const { data: allocations } = useEventAllocations(event.id);
  const [guideOpen, setGuideOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(GUIDE_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const toggleGuide = () => {
    setGuideOpen((v) => {
      try {
        localStorage.setItem(GUIDE_KEY, v ? '1' : '0');
      } catch { /* private browsing */ }
      return !v;
    });
  };
  const closed = event.status !== 'active';

  return (
    <>
      <div className="flex items-start justify-between gap-3 mb-4">
        <p className="text-caption text-ink-subtle">
          {closed
            ? 'Event ini tidak aktif. Alokasi hanya bisa dilihat, tidak bisa diubah.'
            : 'Atur qty yang dibawa ke event, per lot. Penjualan divalidasi terhadap qtyIn − qtySold tiap lot.'}
        </p>
        <ExportStockButton event={event} allocations={allocations ?? []} />
      </div>

      {/* How-to. Collapsible and remembered, because the same person opens
          this page every event and does not need the lesson twice — but the
          first time, the three routes in and one route out were not obvious. */}
      <Card padding="none" className="mb-4 border-primary/20" data-tour="stock-guide">
        <button
          type="button"
          onClick={toggleGuide}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
          aria-expanded={guideOpen}
        >
          <span className="inline-flex items-center gap-2 text-small font-medium text-ink">
            <HelpCircle size={16} className="text-primary" />
            Cara memasukkan dan mengecek stok event
          </span>
          {guideOpen ? <ChevronUp size={16} className="text-ink-subtle" /> : <ChevronDown size={16} className="text-ink-subtle" />}
        </button>
        {guideOpen && (
          <div className="px-4 pb-4 grid gap-4 md:grid-cols-3 text-small text-ink-subtle">
            <div>
              <p className="font-medium text-ink mb-1">1 · Masukkan stok</p>
              <p>
                <b>Add product</b> untuk beberapa produk sekaligus — centang produknya, lalu isi
                lot, expired date, dan qty per lot (satu produk boleh beberapa lot).
              </p>
              <p className="mt-1">
                <b>Import dari Excel</b> untuk daftar panjang: unduh template Excel-nya dulu,
                isi, unggah. Kolom: <code>SKU, Qty, Lot number, Expired date, Satuan, Form order</code>.
                Tanggal ditulis <code>12-Nov-2030</code>.
              </p>
            </div>
            <div>
              <p className="font-medium text-ink mb-1">2 · Ubah kalau perlu</p>
              <p>
                Selama lot itu belum ada yang terjual, semuanya masih bisa diubah — qty, lot,
                expired date, satuan, form order. Begitu satu unit terjual, datanya sudah tercetak
                di invoice customer, jadi hanya qty yang bisa disesuaikan.
              </p>
              <p className="mt-1">
                Import ulang SKU dengan lot dan tanggal yang sama akan <b>memperbarui qty</b>, bukan
                menggandakan.
              </p>
            </div>
            <div>
              <p className="font-medium text-ink mb-1">3 · Cek stok</p>
              <p>
                <b>Export stok</b> mengunduh Excel berisi qty masuk, terjual, dan sisa per lot —
                itulah lembar yang dibawa untuk menghitung fisik di lokasi.
              </p>
              <p className="mt-1">
                Kasir hanya bisa menjual produk yang ada di halaman ini; yang tidak dialokasikan
                tidak muncul di POS.
              </p>
            </div>
          </div>
        )}
      </Card>

      <EventAllocationsManager eventId={event.id} />
    </>
  );
}
